import flow from 'lodash/flow'
import get from 'lodash/get'
import mapValues from 'lodash/mapValues'
import {SubmissionError} from 'redux-form/lib/SubmissionError'

import {onStatus} from '../../api/helpers'
import * as report from '../../report'
import * as blackDesk from '../../store/blackDesk'
import {
  renameVendorApiFieldsToFormFields,
  renameAddressFormFieldsToApiFields,
  renameAddressApiFieldsToFormFields,
} from './blackDeskUtils'

export const getJson = (response) => get(response, 'json')

// ERRORS
// ___________________________________________________________

const CONTACT = '(310) 438-5917 or jess@theblacktux.com'
export const UNAUTHORIZED_ERROR = 'Unauthorized'
export const SERVER_ERROR = `We are currently experiencing technical difficulties. If the issue
persists, please reach out at ${CONTACT}.`
export const UNEXPECTED_ERROR = `An unexpected error occurred. If the issue persists, please reach out at ${CONTACT}.`
export const ALREADY_REGISTERED_ERROR = 'This email is already registered with The Black Desk.'

// Throws a form-wide error (not tied to a specific field)
export const throwFormSubmissionError = (errorMessage) => {
  throw new SubmissionError({_error: errorMessage})
}

// Throws field-level errors
export const throwFieldSubmissionError = (errors) => {
  throw new SubmissionError(errors)
}

export const throwError = (error) => {
  if (error instanceof SubmissionError) {
    throw error
  }
  report.exception(error) // logs connection and server errors to Sentry
  throwFormSubmissionError(UNEXPECTED_ERROR)
}

export const joinErrorMessages = (errors) => mapValues(errors, (error) => error.join(', '))

// VENDOR FORM FUNCTIONS
// ___________________________________________________________

// Change the wording of the error so it's more undersandable to the user
export const changeUniqueEmailErrorMessage = (errors) => {
  let shimmedErrors = errors
  const emailErrors = errors.email
  if (emailErrors) {
    const newErrors = emailErrors.map((error) =>
      error.replace('This field must be unique.', ALREADY_REGISTERED_ERROR),
    )
    shimmedErrors = {...errors, email: newErrors}
  }

  return shimmedErrors
}

// Transform errors from the server into a format that redux form can understand
export const formatVendorFormClientErrors = flow([
  getJson,
  renameVendorApiFieldsToFormFields,
  changeUniqueEmailErrorMessage,
  joinErrorMessages,
])

export const formatAndThrowClientVendorErrors = (response) => {
  const fieldErrors = formatVendorFormClientErrors(response)
  throwFieldSubmissionError(fieldErrors)
}

export const handleVendorResponse = onStatus({
  OK: getJson,
  400: formatAndThrowClientVendorErrors,
  401: () => throwFormSubmissionError(UNAUTHORIZED_ERROR),
  DEFAULT: () => throwFormSubmissionError(SERVER_ERROR),
})

// ADDRESS FORM FUNCTIONS
// ___________________________________________________________

export const getVendorInfoForPayload = (state) => ({
  first_name: blackDesk.vendorFormValues(state, 'firstName'),
  last_name: blackDesk.vendorFormValues(state, 'lastName'),
  phone: blackDesk.vendorFormValues(state, 'phone'),
})

/*
  Since the Address table takes in first name, last name, and phone fields,
  we need to get this info from the vendor form and add it to the address payload.
*/
export const getAddressPayload = (formValues, state) => {
  const vendorInfoForPayload = getVendorInfoForPayload(state)
  const address = renameAddressFormFieldsToApiFields(formValues)
  return {...address, ...vendorInfoForPayload}
}

export const formatAddressFormClientErrors = flow([
  getJson,
  renameAddressApiFieldsToFormFields,
  joinErrorMessages,
])

export const formatAndThrowClientAddressErrors = (response) => {
  const fieldErrors = formatAddressFormClientErrors(response)
  throwFieldSubmissionError(fieldErrors)
}

export const handleAddressResponse = onStatus({
  OK: getJson,
  400: formatAndThrowClientAddressErrors,
  401: () => throwFormSubmissionError(UNAUTHORIZED_ERROR),
  DEFAULT: () => throwFormSubmissionError(SERVER_ERROR),
})
